import { Link } from 'react-router-dom';
import { useExpenses } from '../contexts/ExpenseContext';
import { CATEGORIES, MONTHS } from '../types';
import { 
  FileText, 
  Camera, 
  ArrowRight,
  Loader2,
  HardDrive,
  DollarSign,
  Calendar,
  TrendingUp
} from 'lucide-react';

export default function Dashboard() {
  const { expenses, stats, isLoading } = useExpenses();

  const currentMonth = new Date().getMonth() + 1;
  const currentYear = new Date().getFullYear();

  const thisMonthExpenses = expenses.filter(
    e => e.month === currentMonth && e.year === currentYear
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Welcome Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">
            Tax Expense Tracker
          </h1>
          <p className="text-shuttle-sky mt-1">
            {MONTHS[currentMonth - 1]} {currentYear} • {thisMonthExpenses.length} documents this month
          </p>
        </div>
        <Link
          to="/scanner"
          className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-shuttle-sand to-amber-500 text-shuttle-dark rounded-xl font-medium hover:from-amber-400 hover:to-amber-500 transition-all shadow-lg"
        >
          <Camera className="w-5 h-5" />
          <span>Scan Receipt</span>
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
              <FileText className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats?.totalExpenses || 0}</p>
              <p className="text-sm text-white/60">Total Documents</p>
            </div>
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">
                ${(stats?.totalAmount || 0).toLocaleString()}
              </p>
              <p className="text-sm text-white/60">Total Tracked</p>
            </div>
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
              <Calendar className="w-6 h-6 text-purple-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{thisMonthExpenses.length}</p>
              <p className="text-sm text-white/60">This Month</p>
            </div>
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-orange-500/20 rounded-xl flex items-center justify-center">
              <HardDrive className="w-6 h-6 text-orange-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats?.storageUsedFormatted || '0 KB'}</p>
              <p className="text-sm text-white/60">Storage Used</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Link
          to="/scanner"
          className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all group"
        >
          <Camera className="w-8 h-8 text-shuttle-sand mb-3" />
          <p className="font-medium text-white">Scan Receipt</p>
          <p className="text-sm text-white/60">Use camera</p>
        </Link>

        <Link
          to="/bol-send"
          className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all group"
        >
          <span className="text-3xl mb-2 block">📋</span>
          <p className="font-medium text-white">Send BOL</p>
          <p className="text-sm text-white/60">Email to Bennett</p>
        </Link>

        <Link
          to="/upload"
          className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all group"
        >
          <TrendingUp className="w-8 h-8 text-green-400 mb-3" />
          <p className="font-medium text-white">Upload File</p>
          <p className="text-sm text-white/60">From device</p>
        </Link>

        <Link
          to="/export"
          className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all group"
        >
          <span className="text-3xl mb-2 block">📦</span>
          <p className="font-medium text-white">Export PDF</p>
          <p className="text-sm text-white/60">For CPA</p>
        </Link>
      </div>

      {/* Categories Grid */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-white">By Category</h2>
          <Link 
            to="/expenses" 
            className="text-sm text-shuttle-sky hover:text-white flex items-center space-x-1"
          >
            <span>View all</span>
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {CATEGORIES.map((cat) => {
            const count = stats?.byCategory?.[cat.value] || 0;
            return (
              <Link
                key={cat.value}
                to={`/expenses?category=${cat.value}`}
                className="bg-white/10 backdrop-blur-md rounded-2xl p-5 border border-white/20 hover:bg-white/20 transition-all group"
              >
                <div className="text-3xl mb-2">{cat.icon}</div>
                <p className="text-2xl font-bold text-white">{count}</p>
                <p className="text-sm text-white/60 truncate">{cat.label}</p>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Recent Documents */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-white">Recent Documents</h2>
        </div>
        <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 overflow-hidden">
          {expenses.length > 0 ? (
            <div className="divide-y divide-white/10">
              {expenses.slice(0, 5).map((expense) => {
                const category = CATEGORIES.find(c => c.value === expense.category);
                return (
                  <div
                    key={expense.id}
                    className="flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
                  >
                    <div className="flex items-center space-x-4">
                      <span className="text-2xl">{category?.icon || '📄'}</span>
                      <div>
                        <p className="font-medium text-white truncate max-w-xs">
                          {expense.filename}
                        </p>
                        <p className="text-sm text-white/60">
                          {expense.description || category?.label} • {new Date(expense.date).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className={`category-badge category-${expense.category}`}>
                        {category?.label}
                      </span>
                      {expense.amount && (
                        <span className="text-white font-medium">
                          ${expense.amount.toFixed(2)}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-12 text-center text-white/60">
              <FileText className="w-12 h-12 mx-auto mb-4 text-white/30" />
              <p>No documents yet</p>
              <Link
                to="/scanner"
                className="inline-flex items-center space-x-2 mt-4 text-shuttle-sky hover:text-white"
              >
                <Camera className="w-4 h-4" />
                <span>Scan your first receipt</span>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}





